<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0"
            title="Time to earliest treatment record among all patients, by treatment type">
      <v-card-subtitle class="text-wrap">{{ treatmentProbabilityChartComment }}</v-card-subtitle>
      <v-row no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0">
            <GChart type="LineChart" :data="treatmentProbabilityChartData"
                    :options="treatmentProbabilityChartOptions"
                    :key="'treatmentProbabilityChart' + treatmentProbabilityChartKeyIndex"
                    v-if="treatmentProbabilityChartData.length !== 0"/>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="0" lg="4" xl="4" xxl="4"/>
        <v-col cols="12" lg="4" xl="4" xxl="4">
          <v-card class="mx-auto" elevation="0" title="">
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="medianYearsToEarliestTreatmentHeaders"
                              :items="medianYearsToEarliestTreatmentData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="4" xl="4" xxl="4"/>
      </v-row>
      <v-row>
        <v-col cols="12" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" elevation="0" title="Time to earliest treatment record among treated patients, by treatment type">
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="treatedPatientsMedianDaysToEarliestTreatmentHeaders"
                              :items="treatedPatientsMedianDaysToEarliestTreatmentData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-card class="mx-auto" elevation="0" title="Sankey diagram">
          <v-row align="center" class="mb-8" no-gutters>
            <v-col cols="2">
              <v-combobox class="mr-2" v-model="selectedMaximumTreatmentPhase" :items="selectMaximumTreatmentPhaseItems"
                          label="Maximum number of treatment phases to display"
                          hint="Controls the maximum number of treatment phases to display. Used to display additional
                      Subsequent phases."
                          :persistent-hint="true"></v-combobox>
            </v-col>
            <v-col cols="2">
              <v-combobox class="mr-2" v-model="selectedDataset" :items="selectDatasetItems"
                          label="Treatment pattern period"
                          hint="Display treatment pattern during the whole follow-up period, or only within the first
                      6 or 12 months after index date."
                          :persistent-hint="true"></v-combobox>
            </v-col>
            <v-col cols="2">
              <v-checkbox v-model="panAndZoomEnabled" label="Enable pan and zoom"
                          hide-details></v-checkbox>
            </v-col>
            <v-col cols="6">
              <v-spacer></v-spacer>
            </v-col>
          </v-row>
          <v-row class="mb-8" align="center" no-gutters>
            <v-col cols="12">
              <GChart :settings="{ packages: ['sankey'] }" type="Sankey"
                      :data="sanKeyChartData"
                      :options="sanKeyChartOptions"
                      id="sanKeyChart"
                      @ready="sanKeyChartReady"
                      v-if="sanKeyChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
            </v-col>
          </v-row>
          <v-row align="start" class="mb-8" no-gutters>
            <v-col cols="5">
              <v-data-table
                  :headers="[{title: 'Description', align: 'start', key: 'line', sortable: false}]"
                  :items="treatmentDescription.map(data => ({line: data}))"
                  density="compact"
                  :fixed-header="true"
                  :items-per-page="0"
                  class="no-row-borders">
                <template #bottom></template>
                <template #no-data></template>
                <template v-slot:item="{ item }">
                  <div v-if="item.line !== ''" class="ms-4">
                    {{ item.line }}
                  </div>
                  <div :style="{ height: '12px'}" v-else></div>
                </template>
              </v-data-table>
            </v-col>
            <v-col cols="1">
              <v-divider vertical></v-divider>
            </v-col>
            <v-col cols="6">
              <div>
                <v-data-table :headers="treatmentLegendHeaders"
                              :items="treatmentLegend"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="no-row-borders allow-line-breaks-and-tabs striped-table">
                  <template #headers="{ columns }">
                    <tr>
                      <th colspan="2" class="text-left">{{columns[0].title}}</th>
                      <th></th>
                    </tr>
                  </template>
                  <template #bottom></template>
                  <template #no-data></template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import svgPanZoom from 'svg-pan-zoom';

export default defineComponent({
  name: 'InternalOrderCancerTreatmentPaths',
  props: {
    icd: String,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    treatmentDescription: [],
    treatmentLegendHeaders: [
      {
        title: 'Procedure/ICD-10/ATC codes used to identify surgery, radiotherapy, and chemotherapy',
        align: 'start',
        key: 'treatment_name',
        sortable: false
      },
      {
        title: '',
        align: 'start',
        key: 'treatment_description',
        sortable: false
      }
    ],
    treatmentLegend: [],
    selectMaximumTreatmentPhaseItems: [
      {
        title: 'Two',
        value: 3
      },
      {
        title: 'Three',
        value: 4
      },
      {
        title: 'Four',
        value: 5
      },
      {
        title: 'Five',
        value: 6
      },
      {
        title: 'Six',
        value: 7
      },
      {
        title: 'Seven',
        value: 8
      },
      {
        title: 'Eight',
        value: 9
      },
      {
        title: 'Nine',
        value: 10
      },
      {
        title: 'Ten',
        value: 11
      },
      {
        title: 'Eleven',
        value: 12
      },
      {
        title: 'Twelve',
        value: 13
      },
      {
        title: 'Thirteen',
        value: 14
      },
      {
        title: 'Fourteen',
        value: 15
      },
      {
        title: 'Fifteen',
        value: 16
      },
      {
        title: 'Sixteen',
        value: 17
      },
      {
        title: 'Seventeen',
        value: 18
      },
      {
        title: 'Eighteen',
        value: 19
      },
      {
        title: 'Nineteen',
        value: 20
      },
      {
        title: 'Twenty',
        value: 21
      }
    ],
    selectedMaximumTreatmentPhase: {
      title: 'Three',
      value: 4
    },
    selectDatasetItems: [
      {
        title: 'Whole follow-up period',
        value: 'All'
      },
      {
        title: 'First 12 months',
        value: '12m'
      },
      {
        title: 'First 6 months',
        value: '6m'
      }
    ],
    selectedDataset: {
      title: 'Whole follow-up period',
      value: 'All'
    },
    panAndZoomEnabled: false,
    sanKeyPanZoom: undefined,
    sanKeyChartData: [],
    sanKeyChartOptions: {
      height: 800,
      sankey: {
        node: {
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          label: {
            fontSize: 14,
            color: '#ffffff',
            bold: true
          }
        },
        link: {
          colorMode: 'gradient',
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          interactivity: true
        }
      },
      tooltip: {
        isHtml: true
      }
    },
    treatmentProbabilityChartKeyIndex: 0,
    treatmentProbabilityChartData: [],
    treatmentProbabilityChartComment: '',
    treatmentProbabilityChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      hAxis: {
        title: 'Follow-up (years)',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Treatment probability',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.25
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: {'style': 'area'},
      tooltip: {
        isHtml: true
      }
    },
    medianYearsToEarliestTreatmentHeaders: [
      {
        title: 'Treatment',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Median years (95% confidence interval) to earliest treatment among all patients',
        align: 'start',
        key: 'median_years',
      }
    ],
    medianYearsToEarliestTreatmentData: [],
    medianYearsToEarliestTreatmentComment: '',
    treatedPatientsMedianDaysToEarliestTreatmentHeaders: [
      {
        title: 'Treatment',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Number (%) of patients getting treatment',
        align: 'start',
        key: 'number_of_patients',
      },
      {
        title: 'Median days (95% confidence interval) to earliest treatment among treated patients',
        align: 'start',
        key: 'median_days',
      }
    ],
    treatedPatientsMedianDaysToEarliestTreatmentData: [],
    treatedPatientsMedianDaysToEarliestTreatmentComment: ''
  }),
  watch: {
    selectedMaximumTreatmentPhase() {
      this.loadDynamicData()
    },
    selectedDataset() {
      this.loadDynamicData()
    },
    panAndZoomEnabled() {
      this.loadDynamicData()
    }
  },
  methods: {
    forceRerender() {
      this.treatmentProbabilityChartKeyIndex += 1;
    },
    async loadTreatmentDescription() {
      let data = axios.get('cancer-internal-order/treatment-description', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd
        }
      });
      return data.then((response) => {
        this.treatmentDescription = response.data
      });
    },
    async loadTreatmentLegend() {
      let data = axios.get('cancer-internal-order/treatment-legend', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd
        }
      });
      return data.then((response) => {
        this.treatmentLegend = response.data
      });
    },
    async loadSanKeyChartData() {
      let postData = {
        icd: this.icd,
        maximum_treatment_phase: this.selectedMaximumTreatmentPhase.value,
        dataset: this.selectedDataset.value
      }
      return axios.post('cancer-internal-order/sankey-data',
          postData, {
            headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`}
          });
    },
    async loadTreatmentProbabilityData() {
      let data = axios.get('/cancer-internal-order/treatment-probability',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.treatmentProbabilityChartData = response.data.data
        this.treatmentProbabilityChartComment = response.data.comment
      });
    },
    async loadMedianYearsToEarliestTreatmentData() {
      let data = axios.get('cancer-internal-order/median-years-to-earliest-treatment-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.medianYearsToEarliestTreatmentData = response.data.data
        this.medianYearsToEarliestTreatmentComment = response.data.comment
      });
    },
    async loadTreatedPatientsMedianDaysToEarliestTreatmentData() {
      let data = axios.get('cancer-internal-order/treated-patients-median-days-to-earliest-treatment-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.treatedPatientsMedianDaysToEarliestTreatmentData = response.data.data
        this.treatedPatientsMedianDaysToEarliestTreatmentComment = response.data.comment
      });
    },
    async sanKeyChartReady(chart, google) {
      const getPanAndZoomEnabled = () => this.panAndZoomEnabled
      const getSanKeyPanZoom = () => this.sanKeyPanZoom
      const setSanKeyPanZoom = (panZoom) => {
        this.sanKeyPanZoom = panZoom
      }
      const readyHandler = function () {
        const svg = document.querySelector('#sanKeyChart svg');
        if (svg && getPanAndZoomEnabled()) {
          if (getSanKeyPanZoom()?.destroy) {
            getSanKeyPanZoom().destroy();
          }
          setSanKeyPanZoom(svgPanZoom(svg, {
            controlIconsEnabled: true,
            fit: true,
            center: true,
            minZoom: 0.5,
            maxZoom: 10,
            zoomScaleSensitivity: 0.3
          }));
        }
      }

      google.visualization.events.addListener(chart, 'ready', readyHandler)
    },
    async loadStaticData() {
      await Promise.all([
        this.loadTreatmentDescription(),
        this.loadTreatmentLegend(),
        this.loadTreatmentProbabilityData(),
        this.loadMedianYearsToEarliestTreatmentData(),
        this.loadTreatedPatientsMedianDaysToEarliestTreatmentData()
      ]).then(() => {
        console.log('Static data loaded: InternalOrderCancerTreatmentPaths')
      })
    },
    async loadDynamicData() {
      await this.loadSanKeyChartData().then(response => {
        this.sanKeyChartData = response.data;
        console.log('Dynamic data loaded: InternalOrderCancerTreatmentPaths')
      })
    }
  },
  mounted() {
    this.loadStaticData().then(() => {
      return this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.forceRerender()
      this.loadDynamicData()
    }
  }
});
</script>

<style scoped>
.no-row-borders :deep(.v-data-table__tr) {
  border-bottom: none !important;
  box-shadow: none !important;
}

.no-row-borders :deep(.v-data-table__td) {
  border-bottom: none !important;
  box-shadow: none !important;
}

.no-row-borders :deep(.v-data-table__th) {
  border-bottom: none !important;
}

.allow-line-breaks-and-tabs :deep(.v-data-table__td) {
  white-space: pre-wrap;
}

.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}
</style>
